import { Badge, Button, Card, EmptyState, Field, Input } from '@personachat/ui';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Coins } from 'lucide-react';
import { useId, useMemo, useState, type FormEvent } from 'react';

import { api } from '../../api/client';
import { lire } from '../../api/call';
import type { components } from '../../api/schema';
import { DataTable, type Colonne } from '../../components/DataTable';
import { ErreurChargement, Section, Select } from '../../components/bits';
import { ConfirmDialog } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { nouvelleCle } from '../../lib/fichiers';
import { formatCredits, formatDate, formatDateTime, formatDelta, formatEuros, formatNombre } from '../../lib/format';
import { libelleMouvement, LIBELLE_RAISON, RAISONS, type RaisonMouvement } from '../../lib/labels';
import { RaisonEchec } from '../credits/RaisonEchec';

type Fiche = components['schemas']['UserDetail'];
type Mouvement = components['schemas']['LedgerEntry'];

const STATUT_ABONNEMENT: Record<string, string> = { ACTIVE: 'Actif', INCOMPLETE: 'Incomplet', PAST_DUE: 'Paiement en retard', CANCELED: 'Résilié' };
const STATUT_PAIEMENT: Record<string, string> = { SUCCEEDED: 'Payé', PENDING: 'En attente', FAILED: 'Échoué', REFUNDED: 'Remboursé' };

export function useAllocation() {
  return useQuery({
    queryKey: ['credits', 'allocation'],
    queryFn: async () => (await lire(api.GET('/api/admin/credits/allowance'))).allowance,
    staleTime: 10_000,
  });
}

/** Onglet Crédits : solde, ajout ou retrait avec motif, historique complet, achats. */
export function CreditsTab({ eleve }: { eleve: Fiche }) {
  const [page, setPage] = useState(1);
  const [raison, setRaison] = useState<RaisonMouvement | ''>('');

  const historique = useQuery({
    queryKey: ['eleve', eleve.id, 'ledger', page, raison],
    queryFn: () =>
      lire(
        api.GET('/api/admin/users/{id}/ledger', {
          params: { path: { id: eleve.id }, query: { page, limit: 15, ...(raison ? { reason: raison } : {}) } },
        })
      ),
    placeholderData: keepPreviousData,
  });

  const achats = useQuery({
    queryKey: ['eleve', eleve.id, 'billing'],
    queryFn: () => lire(api.GET('/api/admin/users/{id}/billing', { params: { path: { id: eleve.id } } })),
  });

  const colonnes: Colonne<Mouvement>[] = [
    { id: 'date', header: 'Date', cell: (m) => <span className="nowrap">{formatDateTime(m.createdAt)}</span> },
    {
      id: 'mouvement',
      header: 'Mouvement',
      align: 'right',
      cell: (m) => <span className={`num ${m.delta > 0 ? 'pos' : 'neg'}`}>{formatDelta(m.delta)}</span>,
    },
    {
      id: 'raison',
      header: 'Raison',
      cell: (m) => (
        <div>
          <div>{libelleMouvement(m)}</div>
          <RaisonEchec m={m} />
          {(m.motif || m.admin) && (
            <div className="cell-sub">
              {m.motif && `« ${m.motif} »`}
              {m.admin?.email && ` · ${m.admin.email}`}
            </div>
          )}
        </div>
      ),
    },
    { id: 'solde', header: 'Solde après', align: 'right', cell: (m) => <span className="num">{formatNombre(m.balanceAfter)}</span> },
  ];

  const solde = historique.data?.balance ?? eleve.creditWallet?.balance ?? 0;
  const illimite = historique.data?.isUnlimited ?? eleve.creditWallet?.isUnlimited ?? false;
  const abonnement = achats.data?.subscriptions.find((s) => s.status === 'ACTIVE' || s.status === 'PAST_DUE');

  return (
    <>
      <div className="grid-2" style={{ marginBottom: 20 }}>
        <Card>
          <div className="kpi__label">Solde</div>
          <div className="big-number">{illimite ? 'Illimité' : formatNombre(solde)}</div>
          {illimite && <p className="note">Compte illimité (admin) : les générations sont tracées sans être décomptées.</p>}
          {abonnement && (
            <p className="note">
              Abonnement {abonnement.plan.name} ({STATUT_ABONNEMENT[abonnement.status]}) : {formatCredits(abonnement.plan.creditsPerPeriod)} par{' '}
              {abonnement.plan.interval === 'YEAR' ? 'an' : 'mois'}
              {abonnement.currentPeriodEnd && `, prochain renouvellement le ${formatDate(abonnement.currentPeriodEnd)}`}
              {abonnement.cancelAtPeriodEnd && ' (résiliation programmée)'}.
            </p>
          )}
        </Card>
        <AjustementCredits eleve={eleve} solde={solde} illimite={illimite} />
      </div>

      <Section
        title="Historique"
        actions={
          <Select
            aria-label="Filtrer par raison"
            value={raison}
            onChange={(e) => {
              setRaison(e.target.value as RaisonMouvement | '');
              setPage(1);
            }}
          >
            <option value="">Toutes les raisons</option>
            {RAISONS.map((r) => (
              <option key={r} value={r}>
                {LIBELLE_RAISON[r]}
              </option>
            ))}
          </Select>
        }
      >
        {historique.isError ? (
          <ErreurChargement erreur={historique.error} onReessayer={() => void historique.refetch()} />
        ) : (
          <DataTable
            libelle="Historique des crédits"
            colonnes={colonnes}
            lignes={historique.data?.entries}
            cleLigne={(m) => m.id}
            chargement={historique.isFetching && !historique.data}
            pagination={historique.data ? { page: historique.data.pagination.page, pages: historique.data.pagination.pages, total: historique.data.pagination.total } : undefined}
            onPage={setPage}
            vide={<EmptyState icon={<Coins size={24} aria-hidden="true" />} title="Aucun mouvement" text={raison ? 'Aucun mouvement pour cette raison.' : undefined} />}
          />
        )}
      </Section>

      <Section title="Achats">
        {achats.isError ? (
          <ErreurChargement erreur={achats.error} onReessayer={() => void achats.refetch()} />
        ) : !achats.data ? (
          <p className="note">Chargement…</p>
        ) : achats.data.transactions.length === 0 ? (
          <p className="note">Aucun achat.</p>
        ) : (
          <ul className="plain-list">
            {achats.data.transactions.map((t) => (
              <li key={t.id}>
                <span className="nowrap">{formatDateTime(t.createdAt)}</span> · {t.kind === 'PACK' ? (t.pack?.name ?? 'Pack') : 'Abonnement'} ·{' '}
                <strong>{formatEuros(t.amountCents)}</strong> · {formatCredits(t.creditsGranted)}{' '}
                <Badge tone={t.status === 'SUCCEEDED' ? 'success' : t.status === 'FAILED' ? 'danger' : 'neutral'}>{STATUT_PAIEMENT[t.status]}</Badge>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </>
  );
}

/** Ajouter ou retirer des crédits : montant, motif, puis confirmation avec le nouveau solde. */
function AjustementCredits({ eleve, solde, illimite }: { eleve: Fiche; solde: number; illimite: boolean }) {
  const id = useId();
  const toast = useToast();
  const queryClient = useQueryClient();
  const allocation = useAllocation();
  const [sens, setSens] = useState<'ajout' | 'retrait'>('ajout');
  const [montant, setMontant] = useState('');
  const [motif, setMotif] = useState('');
  const [confirmer, setConfirmer] = useState(false);

  const n = Number(montant);
  const valide = Number.isInteger(n) && n >= 1 && motif.trim().length >= 3;
  const delta = sens === 'ajout' ? n : -n;
  const nouveauSolde = solde + delta;
  // Une opération = une clé : un double envoi de la même confirmation ne crédite qu'une fois.
  const cle = useMemo(() => nouvelleCle(), [sens, montant, motif, confirmer]);

  const a = allocation.data;
  const depasse = sens === 'ajout' && a && !a.illimite && (n > a.parOperation || n > a.resteAujourdhui);
  const retraitImpossible = sens === 'retrait' && !illimite && n > solde;

  const soumettre = (e: FormEvent) => {
    e.preventDefault();
    if (valide) setConfirmer(true);
  };

  return (
    <Card>
      <form className="stack" onSubmit={soumettre}>
        <div className="row row--between">
          <strong>Ajuster le solde</strong>
          <div className="segmented" role="group" aria-label="Sens de l’ajustement">
            <button type="button" aria-pressed={sens === 'ajout'} onClick={() => setSens('ajout')}>
              Ajouter
            </button>
            <button type="button" aria-pressed={sens === 'retrait'} onClick={() => setSens('retrait')}>
              Retirer
            </button>
          </div>
        </div>
        <Field
          id={`${id}-montant`}
          label="Crédits"
          error={retraitImpossible ? `Le solde n’est que de ${formatNombre(solde)} crédits.` : null}
          hint={
            sens === 'ajout' && a && !a.illimite
              ? `Plafond : ${formatNombre(a.parOperation)} par opération, encore ${formatNombre(a.resteAujourdhui)} aujourd’hui.`
              : undefined
          }
        >
          <Input
            id={`${id}-montant`}
            inputMode="numeric"
            value={montant}
            onChange={(e) => setMontant(e.target.value.replace(/[^\d]/g, ''))}
            placeholder="50"
            invalid={retraitImpossible}
          />
        </Field>
        <Field id={`${id}-motif`} label="Motif" hint="Obligatoire, visible dans l’historique.">
          <Input id={`${id}-motif`} value={motif} onChange={(e) => setMotif(e.target.value)} maxLength={200} placeholder="Ex. : paiement reçu, webhook en échec" />
        </Field>
        {depasse && <p className="note neg">Au-delà de votre plafond : l’ajout sera refusé, demandez à un super admin.</p>}
        <div>
          <Button type="submit" variant="primary" disabled={!valide || retraitImpossible}>
            {sens === 'ajout' ? 'Ajouter' : 'Retirer'}
            {n > 0 ? ` ${formatCredits(n)}` : ''}
          </Button>
        </div>
      </form>

      <ConfirmDialog
        open={confirmer}
        onOpenChange={setConfirmer}
        title={sens === 'ajout' ? 'Ajouter des crédits' : 'Retirer des crédits'}
        confirmLabel="Confirmer"
        tone={sens === 'ajout' ? 'primary' : 'danger'}
        onConfirm={async () => {
          await lire(
            api.POST('/api/admin/billing/users/{id}/credits', {
              params: { path: { id: eleve.id } },
              body: { delta, motif: motif.trim(), idempotencyKey: cle },
            })
          );
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['eleve', eleve.id] }),
            queryClient.invalidateQueries({ queryKey: ['eleves'] }),
            queryClient.invalidateQueries({ queryKey: ['credits'] }),
            queryClient.invalidateQueries({ queryKey: ['audit', eleve.id] }),
          ]);
          toast.succes(`${formatDelta(delta)} crédits pour ${eleve.email}.`);
          setMontant('');
          setMotif('');
        }}
      >
        <p>
          <strong className={delta > 0 ? 'pos' : 'neg'}>{formatDelta(delta)} crédits</strong> pour <strong>{eleve.email}</strong>
          {!illimite && (
            <>
              , nouveau solde <strong>{formatNombre(nouveauSolde)}</strong>
            </>
          )}
          .
        </p>
        <p className="note">Motif : « {motif.trim()} »</p>
      </ConfirmDialog>
    </Card>
  );
}
